import { pendingThreadMessagesAtom } from "@/atoms/messagesAtoms.ts";
import { parseUnitsExhaustedError } from "@/components/billing/UnitsExhaustedDialog.tsx";
import { buildOptimisticUserMessage } from "@/components/messages/hooks/useSendMessage.tsx";
import { applyEnrichments } from "@/components/messages/Omnibox/applyEnrichments.ts";
import { SmartTextarea } from "@/components/messages/Omnibox/SmartTextarea.tsx";
import { Button } from "@/components/ui/button.tsx";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import {
  filterVisibleEnrichments,
  useOmniboxEnrichment,
} from "@/contexts/OmniboxEnrichmentContext.tsx";
import { createNewTopicThread, updateThreadOptimistically } from "@/hooks/useThreads.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import type { NewMessageReq, NewMessageResp } from "@/types/threadEvents.ts";
import { getThreadPagePath } from "@/components/threads/util.ts";
import { useCalendarPage } from "@/views/CalendarPageContext.tsx";
import { useSendInBackgroundEnabled } from "@/components/messages/Omnibox/useSendInBackground.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { useSetAtom } from "jotai";
import { ArrowUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";

type AgentPromptInputProps = {
  placeholder?: string;
  /**
   * Imperative ref the parent uses to populate the textarea (e.g. when a
   * suggestion chip is clicked). Mirrors the Omnibox's `prefillRef`.
   */
  prefillRef?: React.MutableRefObject<((text: string) => void) | null>;
  /** Called when the user hits Escape. The parent owns dismissal. */
  onEscape?: () => void;
  /**
   * Called after a successful submit so the parent can dismiss the popover
   * + clear the selection. Navigation to the new thread happens here.
   */
  onSubmitted?: () => void;
  /**
   * Extra agent-facing context the popover wants the agent to see on
   * submit (e.g. "About event 'Standup' (Mon 2:00–2:30 PM, 3 guests)").
   * Joined with the enrichment-derived agentContent so both the
   * implicit-view chips and the trigger-specific context land in the
   * same message.
   */
  extraAgentContent?: string;
  /**
   * Optional content rendered ABOVE the textarea, INSIDE the bordered
   * shell. Used by RangeSelectionPopover to host the time-range chip
   * (mirrors Frank's `ComposedPromptInput.chipsSlot`) so the chip and
   * the input read as one card instead of stacked siblings.
   */
  chipsSlot?: React.ReactNode;
  /**
   * What's on screen, shipped with the initial message so the agent can resolve
   * "this"/"it". A thread's composer derives this from its canvas; a composer
   * mounted on a standalone entity view has no canvas and states the entity's
   * URN outright.
   */
  viewContext?: NewMessageReq["viewContext"];
  /**
   * Whether page-scoped enrichments (the calendar's ambient view week) surface
   * here. True for the calendar popovers this was built for — their whole point
   * is to carry that context. False for a composer bound to one entity, where
   * the visible calendar week is noise the agent would read as relevant.
   */
  hostsPageEnrichments?: boolean;
  /**
   * Open the new thread on submit regardless of the send-in-background
   * preference or the submit gesture. The preference exists so the calendar
   * survives a quick aside — there the user is mid-task on a screen worth
   * staying on. A composer on an entity's own page is the opposite: the request
   * *is* the task, and its answer lives in the thread, so staying put would
   * leave the user on a page that never changes.
   */
  alwaysOpenNewThread?: boolean;
};

/**
 * Builds the initial-message request from the trimmed input and the active
 * enrichments. Pure (no I/O) so the enrichment merge and agent-content joining
 * can be unit tested without the surrounding thread-creation side effects.
 */
export function buildInitialMessageReq({
  trimmed,
  enrichments,
  extraAgentContent,
  userTimezone,
  userLocation,
  viewContext,
}: {
  trimmed: string;
  enrichments: Parameters<typeof applyEnrichments>[1];
  extraAgentContent: string | undefined;
  userTimezone: string;
  userLocation: string | null | undefined;
  viewContext?: NewMessageReq["viewContext"];
}): NewMessageReq {
  const enriched = applyEnrichments(trimmed, enrichments);
  const mergedAgentContent =
    [extraAgentContent, enriched.agentContent].filter(Boolean).join("\n") || undefined;
  return {
    text: enriched.text,
    type: "userRequest",
    agentContent: mergedAgentContent,
    calendarContext: enriched.calendarContext,
    viewContext,
    clientContext: {
      userTimezone,
      userLocation: userLocation || undefined,
    },
  };
}

/**
 * Slim prompt-input row for surfaces that start a thread from context rather
 * than from the home Omnibox — the calendar's contextual-agent-trigger popovers
 * and the entity composers built on {@link EntityAgentComposer}. Mirrors the
 * Omnibox's submission pipeline — pulls registered enrichments (selected ranges,
 * implicit view, …) from `OmniboxEnrichmentContext`, applies them via
 * `applyEnrichments`, creates a new thread, sends the initial message, and
 * navigates to `/threads/:id` — but without the Omnibox's toolbar chrome (file
 * attach, model switcher, action button). The host supplies the visible card;
 * this is a textarea + circular send button.
 */
export const AgentPromptInput = ({
  placeholder = "Create event or ask Okthx…",
  prefillRef,
  onEscape,
  onSubmitted,
  extraAgentContent,
  chipsSlot,
  viewContext,
  hostsPageEnrichments = true,
  alwaysOpenNewThread = false,
}: AgentPromptInputProps) => {
  const { enrichments: allEnrichments } = useOmniboxEnrichment();
  const enrichments = useMemo(
    () => filterVisibleEnrichments(allEnrichments, hostsPageEnrichments),
    [allEnrichments, hostsPageEnrichments],
  );
  const { getUserTimezone, getUserLocation } = useCurrentUserContext();
  const calendarPage = useCalendarPage();
  // Whether plain Enter defaults to "send in background" (stay put) vs. opening
  // the new thread. ⌘/Ctrl+Enter always does the opposite. See `handleKeyDown`.
  const backgroundIsDefault = useSendInBackgroundEnabled();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const setPendingThreadMessages = useSetAtom(pendingThreadMessagesAtom);
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isSubmittingRef = useRef(false);

  // Auto-focus on mount so the user can type immediately after selection.
  useEffect(() => {
    textareaRef.current?.focus({ preventScroll: true });
  }, []);

  // Imperative prefill — bypasses the React state dedupe so repeated clicks
  // on the same suggestion chip always refill (and refocus) the textarea.
  useEffect(() => {
    if (!prefillRef) return;
    prefillRef.current = (text: string) => {
      setValue(text);
      textareaRef.current?.setSelectionRange(text.length, text.length);
      textareaRef.current?.focus({ preventScroll: true });
    };
    return () => {
      prefillRef.current = null;
    };
  }, [prefillRef]);

  const sendInitialMessage = useMutation({
    mutationFn: async ({ threadId, body }: { threadId: number; body: NewMessageReq }) => {
      const response = await api.post<NewMessageResp>(`/api/a/threads/${threadId}/messages`, body);
      return response.data;
    },
  });

  const handleSubmit = useCallback(
    async (requestedBackground: boolean = backgroundIsDefault) => {
      if (isSubmittingRef.current) return;
      const trimmed = value.trim();
      if (!trimmed) return;
      // Applied here rather than to `backgroundIsDefault` so it covers every
      // submit gesture — ⌘/Ctrl+Enter passes the inverted default explicitly.
      const background = alwaysOpenNewThread ? false : requestedBackground;
      isSubmittingRef.current = true;
      try {
        const { id: newThreadId } = await createNewTopicThread({ title: "", artifactIds: [] });
        if (!newThreadId) {
          errorToast("Couldn't start the thread");
          return;
        }
        const userLocation = getUserLocation();
        const body = buildInitialMessageReq({
          trimmed,
          enrichments,
          extraAgentContent,
          userTimezone: getUserTimezone(),
          userLocation,
          viewContext,
        });
        const { eventId } = await sendInitialMessage.mutateAsync({
          threadId: newThreadId,
          body,
        });
        // Hand off an optimistic message (status `completed`, queued-pill
        // suppressed) keyed on the real event id so the thread we're about to
        // open seeds it in `useSessionChat` on mount — otherwise the bare POST
        // leaves the message as `status: "queued"` server-side until the actor
        // picks it up, and the user sees it stuck in the queued pill the whole
        // time. Mirrors the home Omnibox's `createNewThreadAndSend` handoff.
        if (eventId !== undefined) {
          const { item: optimisticMessage } = buildOptimisticUserMessage(newThreadId, body, {
            status: "completed",
            suppressQueuedPill: true,
            id: eventId,
          });
          setPendingThreadMessages((prev) => ({ ...prev, [newThreadId]: optimisticMessage }));
        }
        updateThreadOptimistically(queryClient, newThreadId, { activityState: "running" });
        onSubmitted?.();
        if (background) {
          // `background` (set by the submit gesture in handleKeyDown) keeps the
          // user in place rather than opening the new thread. On the calendar
          // screen that's a toast + row flash (`notifyThreadSentInBackground`);
          // elsewhere there's no row to flash, so it's a toast only.
          if (calendarPage) {
            calendarPage.notifyThreadSentInBackground(newThreadId);
          } else {
            confirmationToast("I'm on it", undefined, {
              description: "I'll let you know if I need anything.",
              onToastClick: () => setLocation(getThreadPagePath(newThreadId)),
              actionStatsigName: "send-in-background-toast",
            });
          }
        } else {
          setLocation(getThreadPagePath(newThreadId));
        }
      } catch (err) {
        console.error("AgentPromptInput submit failed:", err);
        // The upgrade dialog already explains a refusal.
        if (!parseUnitsExhaustedError(err)) errorToast("Couldn't start the thread");
      } finally {
        isSubmittingRef.current = false;
      }
    },
    [
      value,
      enrichments,
      sendInitialMessage,
      queryClient,
      setPendingThreadMessages,
      calendarPage,
      backgroundIsDefault,
      setLocation,
      getUserTimezone,
      getUserLocation,
      onSubmitted,
      extraAgentContent,
      viewContext,
      alwaysOpenNewThread,
    ],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>, hasPopupOpen: boolean) => {
      // While a mention / slash / guided-flow popup is open it owns
      // Enter/Tab/Escape/arrows for selection — don't submit or dismiss.
      if (hasPopupOpen) return;
      if (e.key === "Escape") {
        // Let enrichments (e.g. time-range selection) consume Escape first
        // — clicking outside or pressing Esc with a selection should clear
        // the selection rather than dismiss the popover.
        for (const enrichment of enrichments) {
          if (enrichment.onEscape?.()) {
            e.preventDefault();
            return;
          }
        }
        e.preventDefault();
        onEscape?.();
        return;
      }
      // Enter and ⌘/Ctrl+Enter both send; they only differ in where the new
      // thread lands. The `sendInBackground` preference sets the plain-Enter
      // default; ⌘/Ctrl+Enter always does the opposite.
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void handleSubmit(!backgroundIsDefault);
        return;
      }
      // Enter sends (Shift+Enter inserts a newline). Matches the Omnibox's
      // `hideInputControls` Enter behavior — submit even on touch.
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void handleSubmit(backgroundIsDefault);
      }
    },
    [enrichments, handleSubmit, onEscape, backgroundIsDefault],
  );

  const canSubmit = value.trim().length > 0;

  return (
    // Bordered shell with textarea + circular send button on the same row.
    // Mirrors Frank's `ComposedPromptInput` styling — rounded-2xl, popover
    // background, text-base typography. The textarea grows up to ~5 lines
    // and the submit button sits bottom-anchored on the right.
    <div className="relative flex flex-col gap-2 rounded-2xl border bg-popover px-3 py-2">
      {chipsSlot && <div className="flex flex-wrap items-center gap-1.5">{chipsSlot}</div>}
      <div className="flex items-end gap-2">
        <SmartTextarea
          ref={textareaRef}
          value={value}
          onChange={setValue}
          onKeyDown={handleKeyDown}
          initialRows={1}
          placeholder={placeholder}
          className="min-h-7 align-bottom"
        />
        <Button
          type="button"
          size="icon"
          variant="primary"
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
          aria-label="Submit"
          data-statsig-name="agent-popover-submit"
          className="h-7 w-7 shrink-0"
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};
